import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Table, Button, Modal, Badge, Form } from 'react-bootstrap';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { useNavigate } from 'react-router-dom';
import { sendWS, onVideoFrame } from '../services/websocket';
import api from '../services/api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
// Derive media base from API URL: http://localhost:8000/api → http://localhost:8000
const MEDIA_BASE = API_BASE.replace(/\/api\/?$/, '');

// SOS marker icon (red pulsing)
const sosIcon = new L.DivIcon({
  html: '<div style="width:20px;height:20px;border-radius:50%;background:#ff1744;border:3px solid #fff;box-shadow:0 0 10px #ff1744"></div>',
  className: '', iconSize: [20, 20], iconAnchor: [10, 10],
});

const crashIcon = new L.DivIcon({
  html: '<div style="width:16px;height:16px;border-radius:50%;background:#d500f9;border:2px solid #fff;box-shadow:0 0 8px #d500f9"></div>',
  className: '', iconSize: [16, 16], iconAnchor: [8, 8],
});

const STATUS_CFG = {
  active:     { label: 'Actif',    bg: 'danger',  icon: 'bi-exclamation-circle-fill' },
  responding: { label: 'En cours', bg: 'warning', icon: 'bi-telephone-forward' },
  resolved:   { label: 'Résolu',   bg: 'success', icon: 'bi-check-circle' },
};

export default function SOSManagement() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedSos, setSelectedSos] = useState(null);
  const [responseMsg, setResponseMsg] = useState('Aide en route, restez en sécurité.');
  const [videoFrames, setVideoFrames] = useState({});
  const [videoCameras, setVideoCameras] = useState({});
  const [recordingVehicles, setRecordingVehicles] = useState({});
  const [connectingVehicles, setConnectingVehicles] = useState({});
  const [clipSections, setClipSections] = useState({});
  const [crashes, setCrashes] = useState([]);
  const [showMap, setShowMap] = useState(true);
  const canvasRefs = useRef({});        // vehicleId → canvas

  const loadEvents = useCallback(() => {
    api.get('/sos/').then((r) => {
      const data = r.data.results || r.data;
      const order = { active: 0, responding: 1, resolved: 2 };
      setEvents(data.sort((a, b) =>
        (order[a.status] ?? 9) - (order[b.status] ?? 9) || new Date(b.created_at) - new Date(a.created_at)
      ));
    }).catch(() => {});
    api.get('/crashes/').then((r) => {
      setCrashes(r.data.results || r.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadEvents();
    const iv = setInterval(loadEvents, 15000);
    // Instant reload on WS-driven SOS updates
    const onSosUpdate = () => loadEvents();
    window.addEventListener('sos-update', onSosUpdate);
    return () => {
      clearInterval(iv);
      window.removeEventListener('sos-update', onSosUpdate);
    };
  }, [loadEvents]);

  // Live video frames — double-buffered canvas rendering (dual-camera)
  const renderToCanvas = useCallback((vehicleId, base64Frame) => {
    const canvas = canvasRefs.current[vehicleId];
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
    };
    img.src = `data:image/jpeg;base64,${base64Frame}`;
  }, []);

  useEffect(() => {
    const handler = (msg) => {
      if (msg.type === 'started') {
        setConnectingVehicles((prev) => ({ ...prev, [msg.vehicle_id]: true }));
      } else if (msg.type === 'frame') {
        setVideoFrames((prev) => ({ ...prev, [msg.vehicle_id]: true }));
        setConnectingVehicles((prev) => ({ ...prev, [msg.vehicle_id]: false }));
        setVideoCameras((prev) => ({ ...prev, [msg.vehicle_id]: msg.camera }));
        renderToCanvas(msg.vehicle_id, msg.frame);
      } else if (msg.type === 'clip_uploading') {
        setRecordingVehicles((prev) => ({ ...prev, [msg.vehicle_id]: true }));
      } else if (msg.type === 'clip_ready') {
        setRecordingVehicles((prev) => ({ ...prev, [msg.vehicle_id]: false }));
        loadEvents();
      } else if (msg.type === 'stopped') {
        setVideoFrames((prev) => ({ ...prev, [msg.vehicle_id]: false }));
        setConnectingVehicles((prev) => ({ ...prev, [msg.vehicle_id]: false }));

      }
    };
    const unsub = onVideoFrame(handler);
    return unsub;
  }, [loadEvents, renderToCanvas]);

  const loadClips = (sosId) => {
    api.get(`/sos/${sosId}/videos/`).then((r) => {
      setClipSections((prev) => ({ ...prev, [sosId]: r.data }));
    }).catch(() => {});
  };

  const handleRespond = (sos) => {
    sendWS('sos.respond', { vehicle_id: sos.vehicle, message: responseMsg });
    api.post(`/sos/${sos.id}/respond/`).then(() => loadEvents()).catch(() => {});
    setSelectedSos(null);
  };

  const handleResolve = (sosId) => {
    api.post(`/sos/${sosId}/resolve_sos/`).then(() => loadEvents()).catch(() => {});
  };

  const activeEvents = events.filter((e) => e.status === 'active' || e.status === 'responding');

  return (
    <div style={{ padding: 20 }}>
      <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 20 }}>
        <i className="bi bi-shield-exclamation" style={{ color: '#ff1744' }}></i> Gestion des SOS
        <Button
          size="sm"
          variant={showMap ? 'outline-info' : 'info'}
          className="ms-3"
          onClick={() => setShowMap(!showMap)}
        >
          <i className="bi bi-map"></i> {showMap ? 'Masquer' : 'Afficher'} carte
        </Button>
      </h4>

      {/* INTELLIGENT: SOS + Crashes map view */}
      {showMap && (activeEvents.length > 0 || crashes.length > 0) && (
        <div style={{
          height: 280, borderRadius: 12, overflow: 'hidden',
          border: '2px solid #ff1744', marginBottom: 20,
        }}>
          <MapContainer
            center={activeEvents[0] ? [activeEvents[0].lat, activeEvents[0].lng] : [-4.325, 15.322]}
            zoom={12}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {activeEvents.filter(e => e.lat && e.lng).map((sos) => (
              <Marker key={`sos-${sos.id}`} position={[sos.lat, sos.lng]} icon={sosIcon}>
                <Popup>
                  <strong>🆘 {sos.vehicle_plate || `#${sos.vehicle}`}</strong><br />
                  {sos.reason}<br />
                  {new Date(sos.created_at).toLocaleString('fr-FR')}
                </Popup>
              </Marker>
            ))}
            {crashes.filter(c => c.lat && c.lng && !c.false_alarm).slice(0, 20).map((crash) => (
              <Marker key={`crash-${crash.id}`} position={[crash.lat, crash.lng]} icon={crashIcon}>
                <Popup>
                  <strong>💥 Crash {crash.severity}</strong><br />
                  {crash.vehicle_plate} — {crash.deceleration_g}G<br />
                  {crash.speed_before}→{crash.speed_after} km/h<br />
                  {new Date(crash.created_at).toLocaleString('fr-FR')}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}

      {/* Active SOS cards */}
      {activeEvents.length > 0 && (
        <div style={{
          background: 'rgba(255,23,68,0.08)', border: '1px solid #ff1744',
          borderRadius: 12, padding: 16, marginBottom: 24,
        }}>
          <h5 style={{ color: '#ff1744', fontWeight: 700, marginBottom: 12 }}>
            🆘 {activeEvents.length} SOS Actif{activeEvents.length > 1 ? 's' : ''}
          </h5>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            {activeEvents.map((sos) => {
              const cfg = STATUS_CFG[sos.status] || STATUS_CFG.active;
              return (
                <div key={sos.id} style={{
                  background: '#1a1a2e', border: `2px solid ${sos.status === 'active' ? '#ff1744' : '#ff9100'}`,
                  borderRadius: 12, padding: 16, width: 360, flex: '0 0 auto',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>
                      <i className="bi bi-truck" style={{ color: '#00e676', marginRight: 6 }}></i>
                      {sos.vehicle_plate || `Véhicule #${sos.vehicle}`}
                    </span>
                    <Badge bg={cfg.bg} className="px-2">{cfg.label}</Badge>
                  </div>

                  <div style={{ fontSize: 13, color: '#aaa', marginBottom: 10, lineHeight: 1.8 }}>
                    <div><i className="bi bi-chat-dots" style={{ marginRight: 6 }}></i>Raison: <span style={{ color: '#ccc' }}>{sos.reason}</span></div>
                    <div><i className="bi bi-geo-alt" style={{ marginRight: 6 }}></i>Position: {sos.lat?.toFixed(5)}, {sos.lng?.toFixed(5)}</div>
                    <div><i className="bi bi-clock" style={{ marginRight: 6 }}></i>{new Date(sos.created_at).toLocaleString('fr-FR')}</div>
                  </div>

                  {/* Live video feed — front camera (conducteur) */}
                  {videoFrames[sos.vehicle] ? (
                    <div style={{ marginBottom: 10, borderRadius: 10, overflow: 'hidden', position: 'relative', background: '#000' }}>
                      <canvas
                        ref={(el) => { canvasRefs.current[sos.vehicle] = el; }}
                        style={{ width: '100%', borderRadius: 8, maxHeight: 200, objectFit: 'cover', display: 'block', background: '#000' }}
                      />
                      {/* Overlay — top badges */}
                      <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, padding: '6px 8px',
                        background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        {recordingVehicles[sos.vehicle] ? (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            background: '#d50000', color: '#fff', padding: '2px 8px',
                            borderRadius: 12, fontSize: 10, fontWeight: 700,
                          }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ff5252', animation: 'pulse 0.8s infinite' }}></span>
                            REC
                          </span>
                        ) : (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            background: 'rgba(255,23,68,0.9)', color: '#fff', padding: '2px 8px',
                            borderRadius: 12, fontSize: 10, fontWeight: 700,
                          }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', animation: 'pulse 1.2s infinite' }}></span>
                            LIVE
                          </span>
                        )}
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          background: 'rgba(0,230,118,0.8)', color: '#fff', padding: '2px 6px',
                          borderRadius: 12, fontSize: 9, fontWeight: 700,
                        }}>
                          � Conducteur
                        </span>
                      </div>
                    </div>
                  ) : (connectingVehicles[sos.vehicle] || sos.video_active) ? (
                    /* Connecting — pulsing rings (WhatsApp style) */
                    <div style={{
                      height: 140, background: '#0a0a1a', borderRadius: 10,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginBottom: 10, position: 'relative', overflow: 'hidden',
                    }}>
                      <style>{`
                        @keyframes sos-ring-sm1 { 0% { transform: scale(0.8); opacity: 0.5; } 100% { transform: scale(2); opacity: 0; } }
                        @keyframes sos-ring-sm2 { 0% { transform: scale(0.8); opacity: 0.3; } 100% { transform: scale(2.5); opacity: 0; } }
                      `}</style>
                      <div style={{ position: 'relative', width: 60, height: 60 }}>
                        <div style={{
                          position: 'absolute', inset: 0, borderRadius: '50%',
                          border: '2px solid rgba(255,23,68,0.3)',
                          animation: 'sos-ring-sm1 1.8s ease-out infinite',
                        }} />
                        <div style={{
                          position: 'absolute', inset: 0, borderRadius: '50%',
                          border: '2px solid rgba(255,23,68,0.2)',
                          animation: 'sos-ring-sm2 1.8s ease-out infinite 0.4s',
                        }} />
                        <div style={{
                          position: 'absolute', inset: 10, borderRadius: '50%',
                          background: 'linear-gradient(135deg, #ff1744, #d50000)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: '0 0 20px rgba(255,23,68,0.3)',
                        }}>
                          <i className="bi bi-camera-video-fill" style={{ color: '#fff', fontSize: 16 }}></i>
                        </div>
                      </div>
                      <div style={{ marginLeft: 14 }}>
                        <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>Connexion vidéo</div>
                        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>En attente du flux...</div>
                      </div>
                    </div>
                  ) : null}

                  <div className="d-flex gap-2">
                    {sos.status === 'active' && (
                      <Button size="sm" variant="warning" onClick={() => { setSelectedSos(sos); setResponseMsg('Aide en route, restez en sécurité.'); }}>
                        <i className="bi bi-telephone"></i> Répondre
                      </Button>
                    )}
                    <Button size="sm" variant="success" onClick={() => handleResolve(sos.id)}>
                      <i className="bi bi-check-circle"></i> Résolu
                    </Button>
                    <Button size="sm" variant="outline-info" onClick={() => loadClips(sos.id)}>
                      <i className="bi bi-film"></i>
                    </Button>
                    <Button size="sm" variant="outline-light" onClick={() => navigate(`/vehicle/${sos.vehicle}`)}>
                      <i className="bi bi-geo-alt"></i>
                    </Button>
                  </div>

                  {/* Inline clips */}
                  {clipSections[sos.id] && clipSections[sos.id].length > 0 && (
                    <div style={{ marginTop: 10, borderTop: '1px solid #333', paddingTop: 10 }}>
                      <div style={{ fontSize: 12, color: '#ff9100', fontWeight: 600, marginBottom: 6 }}>
                        <i className="bi bi-film"></i> {clipSections[sos.id].length} clips enregistrés
                      </div>
                      <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
                        {clipSections[sos.id].map((clip) => {
                          const videoUrl = clip.video_file?.startsWith('http') ? clip.video_file : `${MEDIA_BASE}${clip.video_file?.startsWith('/') ? '' : '/'}${clip.video_file}`;
                          return (
                            <div key={clip.id} style={{ minWidth: 160 }}>
                              <video src={videoUrl} controls style={{ width: 160, borderRadius: 6 }} />
                              <div style={{ fontSize: 10, color: '#888' }}>
                                👤{' '}
                                {new Date(clip.created_at).toLocaleTimeString('fr-FR')}
                                {clip.duration_sec > 0 && ` — ${Math.round(clip.duration_sec)}s`}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* History table */}
      <h5 style={{ color: '#fff', fontWeight: 600 }}>
        <i className="bi bi-clock-history" style={{ color: '#888', marginRight: 6 }}></i>Historique
      </h5>
      <Table variant="dark" size="sm" style={{ fontSize: 13 }}>
        <thead>
          <tr>
            <th>#</th>
            <th>Véhicule</th>
            <th>Raison</th>
            <th>Statut</th>
            <th>Position</th>
            <th>Date</th>
            <th>Vidéos</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {events.map((sos) => {
            const cfg = STATUS_CFG[sos.status] || { label: sos.status, bg: 'secondary' };
            return (
              <tr key={sos.id}>
                <td>{sos.id}</td>
                <td style={{ fontWeight: 600 }}>{sos.vehicle_plate || `#${sos.vehicle}`}</td>
                <td>{sos.reason}</td>
                <td><Badge bg={cfg.bg}><i className={`bi ${cfg.icon} me-1`}></i>{cfg.label}</Badge></td>
                <td style={{ fontSize: 11 }}>{sos.lat?.toFixed(4)}, {sos.lng?.toFixed(4)}</td>
                <td>{new Date(sos.created_at).toLocaleString('fr-FR')}</td>
                <td>
                  <Button size="sm" variant="outline-info" className="py-0 px-1" onClick={() => loadClips(sos.id)}>
                    <i className="bi bi-film"></i>
                  </Button>
                </td>
                <td>
                  <div className="d-flex gap-1">
                    {sos.status === 'active' && (
                      <Button size="sm" variant="warning" className="py-0" onClick={() => { setSelectedSos(sos); setResponseMsg('Aide en route, restez en sécurité.'); }}>
                        Répondre
                      </Button>
                    )}
                    {(sos.status === 'active' || sos.status === 'responding') && (
                      <Button size="sm" variant="success" className="py-0" onClick={() => handleResolve(sos.id)}>
                        Résolu
                      </Button>
                    )}
                    <Button size="sm" variant="outline-light" className="py-0" onClick={() => navigate(`/vehicle/${sos.vehicle}`)}>
                      <i className="bi bi-eye"></i>
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
          {events.length === 0 && (
            <tr><td colSpan={8} style={{ color: '#555', textAlign: 'center' }}>Aucun SOS enregistré</td></tr>
          )}
        </tbody>
      </Table>

      {/* Clip sections under table */}
      {Object.entries(clipSections).filter(([, clips]) => clips.length > 0).map(([sosId, clips]) => (
        <div key={sosId} style={{
          background: '#1a1a2e', border: '1px solid #333', borderRadius: 12,
          padding: 16, marginTop: 12,
        }}>
          <h6 style={{ color: '#ff9100', fontWeight: 700, marginBottom: 10 }}>
            <i className="bi bi-film"></i> Clips vidéo — SOS #{sosId}
            <Button size="sm" variant="outline-secondary" className="ms-2 py-0"
              onClick={() => setClipSections((prev) => { const n = { ...prev }; delete n[sosId]; return n; })}>
              <i className="bi bi-x"></i>
            </Button>
          </h6>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {clips.map((clip) => {
              const videoUrl = clip.video_file?.startsWith('http') ? clip.video_file : `${MEDIA_BASE}${clip.video_file?.startsWith('/') ? '' : '/'}${clip.video_file}`;
              return (
                <div key={clip.id} style={{ width: 220 }}>
                  <video src={videoUrl} controls style={{ width: '100%', borderRadius: 8 }} />
                  <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                    {new Date(clip.created_at).toLocaleString('fr-FR')}
                    {clip.duration_sec > 0 && ` — ${Math.round(clip.duration_sec)}s`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Response modal */}
      <Modal show={!!selectedSos} onHide={() => setSelectedSos(null)} centered>
        <Modal.Header closeButton style={{ background: '#1a1a2e', borderColor: '#333' }}>
          <Modal.Title style={{ color: '#ff1744', fontSize: 16 }}>
            <i className="bi bi-telephone me-2"></i>
            Répondre au SOS — {selectedSos?.vehicle_plate || `Véhicule #${selectedSos?.vehicle}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#0d0d1a' }}>
          <Form.Label style={{ color: '#aaa', fontSize: 13 }}>Message au conducteur</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={responseMsg}
            onChange={(e) => setResponseMsg(e.target.value)}
            placeholder="Message au conducteur..."
            style={{ background: '#1a1a2e', border: '1px solid #333', color: '#fff' }}
          />
        </Modal.Body>
        <Modal.Footer style={{ background: '#1a1a2e', borderColor: '#333' }}>
          <Button variant="secondary" onClick={() => setSelectedSos(null)}>Annuler</Button>
          <Button variant="danger" onClick={() => handleRespond(selectedSos)}>
            <i className="bi bi-send me-1"></i> Envoyer la réponse
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
